<template>
  <div v-if="isOpen" class="modal-backdrop-custom" @click.self="handleClose">
    <div class="modal-box" style="width: 900px; max-width: 95vw; max-height: 90vh; overflow-y: auto;">
      <div class="d-flex justify-content-between align-items-center mb-3">
        <div>
          <div class="small text-muted">BillName</div>
          <h6 class="fw-bold mb-0">💵 현금 청구서 문자 발송</h6>
          <div class="fw-bold text-dark" style="font-size: 0.95rem; word-break: break-word; max-width: 700px;">
            {{ billName || 'BillName을 선택된 항목에서 자동 생성합니다.' }}
          </div>
        </div>
        <button class="btn-close" @click="handleClose" v-if="!sending"></button>
      </div>

      <!-- 설정 섹션 -->
      <div v-if="!sending" class="bg-light p-3 rounded mb-3">
        <div v-if="completed" class="alert alert-success py-2 mb-3">
          📩 일괄 발송이 완료되었습니다.
        </div>
        <div class="row g-2 mb-3">
          <div class="col-md-4">
            <label class="form-label form-label-sm"><strong>이용 시작일 <span class="text-danger">*</span></strong></label>
            <input v-model="paymentData.startDate" type="date" class="form-control form-control-sm" />
          </div>
          <div class="col-md-4">
            <label class="form-label form-label-sm"><strong>이용 종료일 <span class="text-danger">*</span></strong></label>
            <input v-model="paymentData.endDate" type="date" class="form-control form-control-sm" />
          </div>
          <div class="col-md-4">
            <label class="form-label form-label-sm"><strong>입금 기한</strong></label>
            <input v-model="paymentData.expireDate" type="datetime-local" class="form-control form-control-sm" />
          </div>
        </div>

        <div class="mb-3">
          <label class="form-label form-label-sm"><strong>발송 메시지 미리보기</strong></label>
          <div class="bg-white p-2 rounded" style="border: 1px solid #ddd; min-height: 100px; font-size: 0.85rem; word-break: break-word; white-space: pre-wrap;">
            <div v-if="message">
              <strong>{{ billName }}</strong>
              <div style="margin-top: 0.5rem;">{{ message }}</div>
            </div>
            <span v-else class="text-muted">메시지가 없습니다.</span>
          </div>
          <small class="text-muted mt-1 d-block">※ 실제 발송 시 개인 정보(차량명, 금액, 전화번호)가 포함됩니다.</small>
        </div>

        <div class="mb-3">
          <label class="form-label form-label-sm"><strong>임의 금액 <span class="text-muted">(선택사항 - 미입력 시 계산 금액 사용)</span></strong></label>
          <div class="d-flex align-items-center gap-2">
            <input 
              v-model.number="customPrice" 
              type="number" 
              class="form-control form-control-sm price-input"
              @change="updateExampleMessage"
              @input="updateExampleMessage"
              min="0"
              placeholder="예: 50000"
              style="width: 150px;"
            />
            <button 
              @click="calculateAutoPrice"
              class="btn btn-sm btn-outline-primary"
              title="이용시작일과 이용종료일 기준 자동계산"
            >
              자동계산
            </button>
            <span class="text-muted">원</span>
            <small class="text-muted" v-if="customPrice !== null && customPrice > 0">({{ customPrice.toLocaleString() }})</small>
          </div>
        </div>

        <div class="alert alert-info py-2">
          <strong>발송 대상:</strong> {{ (selectedItems || []).length }}명 | 
          <strong>이용기간:</strong> {{ paymentData.startDate }} ~ {{ paymentData.endDate }} | 
          <strong>입금기한:</strong> {{ paymentData.expireDate || '설정 안함' }}
        </div>

        <div v-if="error" class="alert alert-danger py-2 small mb-2">{{ error }}</div>

        <div class="table-responsive mb-3" v-if="(selectedItems || []).length > 0">
          <table class="table table-sm mb-0">
            <thead>
              <tr>
                <th class="py-2">이름</th>
                <th class="py-2">차량번호</th>
                <th class="py-2 text-end">계산 금액</th>
              </tr>
            </thead>
            <tbody>
              <tr v-for="(item, index) in (selectedItems || [])" :key="item?.WaitingSeq ?? index">
                <td class="py-2">{{ item?.UserName || '' }}</td>
                <td class="py-2">{{ item?.CarNumber || '' }}</td>
                <td class="py-2 text-end text-muted">{{ calculateAmount(item).toLocaleString() }}원</td>
              </tr>
            </tbody>
          </table>
        </div>

        <div class="d-flex gap-2 justify-content-end">
          <button class="btn btn-secondary btn-sm" @click="handleClose">취소</button>
          <button class="btn btn-primary btn-sm" @click="handleConfirm">
            발송 시작 ({{ (selectedItems || []).length }}명)
          </button>
        </div>
      </div>

      <!-- 발송 진행 중 -->
      <div v-if="sending || completed">
        <div class="mb-3">
          <div class="d-flex justify-content-between align-items-center mb-2">
            <strong>{{ completed ? '발송 완료' : '발송 진행 중...' }}</strong>
            <span class="text-muted small">{{ progress.current }} / {{ progress.total }}</span>
          </div>
          <div class="progress">
            <div class="progress-bar progress-bar-striped"
                 :class="completed ? 'bg-success' : 'progress-bar-animated'"
                 :style="{ width: progress.percentage + '%' }">
              {{ progress.percentage }}%
            </div>
          </div>
        </div>

        <div class="bg-light p-3 rounded" style="max-height: 400px; overflow-y: auto; border: 1px solid #ddd;">
          <div v-for="(result, idx) in results" :key="idx" class="mb-2 p-2 bg-white rounded" style="border-left: 3px solid;">
            <div :style="{ borderLeftColor: result.success ? '#28a745' : '#dc3545' }" class="w-100">
              <div class="d-flex justify-content-between align-items-start">
                <div>
                  <strong :style="{ color: result.success ? '#28a745' : '#dc3545' }">
                    {{ result.success ? '✓ 성공' : '✗ 실패' }}
                  </strong>
                  <span class="text-muted small ms-2">{{ result.userId }} ({{ result.phoneNumber }})</span>
                </div>
                <small class="text-muted">PaymentSeq: {{ result.paymentSeq || 'N/A' }}</small>
              </div>
              <small v-if="result.message" class="d-block text-muted mt-1">{{ result.message }}</small>
              <small v-if="result.statusText" class="d-block text-muted">{{ result.statusText }}</small>
            </div>
          </div>
        </div>

        <div class="d-flex gap-2 justify-content-end mt-3">
          <button class="btn btn-secondary btn-sm" @click="handleClose" v-if="completed">
            닫기
          </button>
          <span v-else class="text-muted small">
            발송 중입니다. 창을 닫지 마세요.
          </span>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import axios from 'axios'

export default {
  name: 'CashInvoiceSmsModal',
  props: {
    isOpen: {
      type: Boolean,
      required: true
    },
    selectedItems: {
      type: Array,
      default: () => []
    },
    areas: {
      type: Array,
      default: () => []
    },
    selectedAreaSeq: {
      type: [Number, String],
      default: null
    }
  },
  emits: ['close', 'bulk-sms-sent'],
  data() {
    return {
      paymentData: {
        startDate: '',
        endDate: '',
        expireDate: ''
      },
      billName: '',
      message: '',
      sending: false,
      completed: false,
      progress: { current: 0, total: 0, percentage: 0 },
      results: [],
      error: null,
      customPrice: null
    }
  },
  watch: {
    isOpen(newVal) {
      if (newVal) {
        this.initialize()
      }
    },
    selectedAreaSeq() {
      if (this.isOpen) {
        this.updateExampleMessage()
      }
    },
    'paymentData.startDate'() {
      if (this.isOpen) {
        const sd = new Date(this.paymentData.startDate)
        if (!isNaN(sd.getTime())) {
          const lastDay = new Date(sd.getFullYear(), sd.getMonth() + 1, 0)
          this.paymentData.endDate = this.formatDateForInput(lastDay)
        }
        this.updateExampleMessage()
      }
    },
    'paymentData.endDate'() {
      if (this.isOpen) {
        this.updateExampleMessage()
      }
    },
    'paymentData.expireDate'() {
      if (this.isOpen) {
        this.updateExampleMessage()
      }
    }
  },
  methods: {
    initialize() {
      this.sending = false
      this.completed = false
      this.results = []
      this.error = null
      this.customPrice = null

      // 기본 날짜 설정
      const today = new Date()
      const nextMonth = new Date(today.getFullYear(), today.getMonth() + 1, 1)
      const nextMonthLastDay = new Date(nextMonth.getFullYear(), nextMonth.getMonth() + 1, 0)
      const currentMonthLastDay = new Date(today.getFullYear(), today.getMonth() + 1, 0)
      currentMonthLastDay.setHours(23, 59, 59, 0)

      this.paymentData.startDate = this.formatDateForInput(nextMonth)
      this.paymentData.endDate = this.formatDateForInput(nextMonthLastDay)
      this.paymentData.expireDate = this.formatDateTimeForInput(currentMonthLastDay)

      this.updateExampleMessage()
    },
    updateExampleMessage() {
      if (!Array.isArray(this.selectedItems) || this.selectedItems.length === 0) return
      const exampleItem = this.selectedItems[0]
      if (!exampleItem) return
      this.updateBillName(exampleItem)
      this.updateMessage(exampleItem)
    },
    updateBillName(item) {
      if (!item) return
      const sd = new Date(this.paymentData.startDate)
      if (isNaN(sd.getTime())) return

      const y = sd.getFullYear()
      const m = sd.getMonth() + 1
      this.billName = `${item.AreaName} ${y}년 ${m}월 정기권`
    },
    updateMessage(item) {
      if (!item) return
      const sd = new Date(this.paymentData.startDate)
      if (isNaN(sd.getTime())) return

      // customPrice가 지정되면 그 값 사용, 없으면 계산된 금액 사용
      const calculatedAmount = this.calculateAmount(item)
      const finalAmount = this.customPrice !== null && this.customPrice > 0 ? this.customPrice : calculatedAmount
      const priceText = `${finalAmount}원`
      const selectedArea = this.areas.find(a => a.AreaSeq === parseInt(this.selectedAreaSeq))
      const bankName = selectedArea?.BankName || '은행명'
      const accountNumber = selectedArea?.AccountNumber || '계좌번호'

      this.message = `[정기권 신청]\n${this.billName}님, 정기권 결제를 위한 입금을 요청드립니다.\n\n결제금액: ${priceText}\n은행: ${bankName}\n계좌번호: ${accountNumber}\n\n입금 확인 후 정기권이 정상 처리됩니다.`
    },
    getOriginalAmount(item) {
      return Math.round(Number(item.Price || 0))
    },
    calculateAmount(item) {
      const basePrice = Number(item.Price || 0)
      const discountRate = Number(item.DiscountRate || 0)
      if (discountRate > 0) {
        return Math.round((basePrice * (100 - discountRate)) / 100)
      }
      return Math.round(basePrice)
    },
    onCustomPriceChange() {
      // 음수 값 방지
      if (this.customPrice != null && this.customPrice < 0) {
        this.customPrice = 0
      }
      // 미리보기 메시지 업데이트
      this.updateExampleMessage()
    },
    calculateAutoPrice() {
      // 선택된 항목 확인
      if (!this.selectedItems || this.selectedItems.length === 0) {
        alert('선택된 항목이 없습니다.')
        return
      }

      // 날짜 확인
      const startDate = new Date(this.paymentData.startDate)
      const endDate = new Date(this.paymentData.endDate)
      
      if (isNaN(startDate.getTime()) || isNaN(endDate.getTime())) {
        alert('이용시작일과 이용종료일을 확인해주세요.')
        return
      }

      if (startDate > endDate) {
        alert('이용시작일이 이용종료일보다 클 수 없습니다.')
        return
      }

      // 첫 번째 항목의 실제 결제 기준 금액 = 요금제 + 할인 반영 가격
      const firstItem = this.selectedItems[0]
      const discountedPrice = this.calculateAmount(firstItem)

      // 해당 월의 실제 일수
      const monthDays = new Date(startDate.getFullYear(), startDate.getMonth() + 1, 0).getDate()

      // 1일 이용가격 = 할인 반영 금액 ÷ 월 이용일수
      const dailyPrice = discountedPrice / monthDays

      // 실제 이용일수 (종료일 - 시작일 + 1)
      const actualDays = Math.floor((endDate - startDate) / (1000 * 60 * 60 * 24)) + 1

      // 결제금액 (원단위 반올림)
      const finalAmount = Math.round(dailyPrice * actualDays)

      this.customPrice = finalAmount
      this.updateExampleMessage()
    },
    formatDateForInput(date) {
      const year = date.getFullYear()
      const month = String(date.getMonth() + 1).padStart(2, '0')
      const day = String(date.getDate()).padStart(2, '0')
      return `${year}-${month}-${day}`
    },
    formatDateTimeForInput(date) {
      const d = new Date(date)
      if (isNaN(d.getTime())) return ''
      const year = d.getFullYear()
      const month = String(d.getMonth() + 1).padStart(2, '0')
      const day = String(d.getDate()).padStart(2, '0')
      const hh = String(d.getHours()).padStart(2, '0')
      const mm = String(d.getMinutes()).padStart(2, '0')
      return `${year}-${month}-${day}T${hh}:${mm}`
    },
    handleClose() {
      if (!this.sending) {
        this.$emit('close')
      }
    },
    async handleConfirm() {
      if (!this.paymentData.startDate || !this.paymentData.endDate) {
        this.error = '이용 시작일과 종료일을 입력해주세요.'
        return
      }

      const startDate = new Date(this.paymentData.startDate)
      const endDate = new Date(this.paymentData.endDate)
      if (isNaN(startDate.getTime()) || isNaN(endDate.getTime())) {
        this.error = '시작일 또는 종료일 형식이 올바르지 않습니다.'
        return
      }
      if (endDate < startDate) {
        this.error = '종료일은 시작일 이후여야 합니다.'
        return
      }

      this.sending = true
      this.completed = false
      this.error = null
      this.results = []
      this.progress = { current: 0, total: 0, percentage: 0 }

      try {
        await this.sendBulkSms()
        this.completed = true
        this.$emit('bulk-sms-sent', { results: this.results })
      } catch (e) {
        this.error = e.message || '일괄 발송 중 오류가 발생했습니다.'
      } finally {
        this.sending = false
      }
    },
    async sendBulkSms() {
      if (!(this.selectedItems || []).length) {
        throw new Error('선택된 항목이 없습니다.')
      }

      const messages = []
      const paymentRequests = []

      // Step 1: 각 사람별로 결제 요청 생성 및 메시지 조합
      for (const w of this.selectedItems || []) {
        try {
          const originalAmount = this.getOriginalAmount(w)
          const calculatedAmount = this.calculateAmount(w)
          // 모든 사람에게 동일한 customPrice 적용
          const customAmount = this.customPrice !== null && this.customPrice > 0 ? this.customPrice : calculatedAmount
          const isCustomPrice = customAmount !== calculatedAmount ? 1 : 0
          const sd = new Date(this.paymentData.startDate)
          const y = sd.getFullYear()
          const m = sd.getMonth() + 1
          const billName = `${w.AreaName} ${y}년 ${m}월 정기권`

          // 현금 결제 요청 생성
          const payRes = await axios.post(
            `${process.env.VUE_APP_API_BASE}/api/admin/parking-pass-waiting/${w.WaitingSeq}/payment-request`,
            {
              originalPrice: originalAmount,
              paymentPrice: customAmount,
              isCustomPrice,
              startDate: this.paymentData.startDate,
              endDate: this.paymentData.endDate,
              billName,
              expireDate: this.paymentData.expireDate || null,
              paymentType: 'CASH'
            }
          )

          const payment = payRes.data.payment
          const isNewPayment = payRes.data.isNew || false
          const priceText = `${customAmount}원`

          // 현금 발송 메시지
          const selectedArea = this.areas.find(a => a.AreaSeq === parseInt(this.selectedAreaSeq))
          const bankName = selectedArea?.BankName || '은행명'
          const accountNumber = selectedArea?.AccountNumber || '계좌번호'

          let msgContent = `[정기권 신청]\n${billName}님, 정기권 결제를 위한 입금을 요청드립니다.\n\n결제금액: ${priceText}\n은행: ${bankName}\n계좌번호: ${accountNumber}\n\n입금 확인 후 정기권이 정상 처리됩니다.`

          messages.push({
            phoneNumber: w.Tel.replace(/\D/g, ''),
            msg: msgContent,
            waitingSeq: w.WaitingSeq,
            userId: w.UserID
          })

          paymentRequests.push({
            waitingSeq: w.WaitingSeq,
            paymentSeq: payment?.PaymentSeq,
            billName,
            isNew: isNewPayment,
            startDate: this.paymentData.startDate
          })
        } catch (e) {
          console.error(`[Bulk Cash SMS] 결제요청 생성 실패 (${w.UserID}):`, e)
          this.results.push({
            waitingSeq: w.WaitingSeq,
            userId: w.UserID,
            phoneNumber: w.Tel,
            success: false,
            message: `결제 요청 생성 실패: ${e.message}`,
            statusCode: 500
          })
        }
      }

      // Step 2: 일괄 SMS 발송
      this.progress.total = messages.length
      this.progress.current = 0

      try {
        const bulkRes = await axios.post(`${process.env.VUE_APP_API_BASE}/api/ebpp/send-bulk`, {
          messages
        })

        const bulkResults = bulkRes.data.results || []

        // Step 3: 결과와 결제 정보 결합
        for (const result of bulkResults) {
          const paymentInfo = paymentRequests.find(p => p.waitingSeq === result.waitingSeq)
          const statusText = paymentInfo
            ? `[${paymentInfo.billName} | PaymentSeq: ${paymentInfo.paymentSeq || 'N/A'} | ${paymentInfo.isNew ? '신규 생성' : '기존 사용'} | StartDate: ${paymentInfo.startDate}]`
            : '[결제 정보 없음]'

          this.results.push({
            ...result,
            paymentSeq: paymentInfo?.paymentSeq,
            statusText
          })

          this.progress.current++
          this.progress.percentage = Math.round((this.progress.current / this.progress.total) * 100)

          // 1초 대기 (마지막 제외)
          if (this.progress.current < this.progress.total) {
            await new Promise(resolve => setTimeout(resolve, 1000))
          }
        }

        // Step 4: 상태 일괄 변경
        try {
          const waitingSeqs = (this.selectedItems || []).map(w => w.WaitingSeq)
          const selectedArea = this.areas.find(a => a.AreaSeq === parseInt(this.selectedAreaSeq))
          const areaName = selectedArea?.AreaName || ''
          const sd = new Date(this.paymentData.startDate)
          const y = sd.getFullYear()
          const m = sd.getMonth() + 1
          const statusText = `${areaName} ${y}년 ${m}월 현금청구서 문자 발송 완료`

          await axios.post(`${process.env.VUE_APP_API_BASE}/api/admin/parking-pass-waiting/bulk-status`, {
            waitingSeqs,
            status: statusText
          })
          console.log('[Bulk Cash SMS] 상태 변경 완료:', statusText)
        } catch (statusErr) {
          console.error('[Bulk Cash SMS] 상태 변경 실패:', statusErr)
        }
      } catch (e) {
        this.error = `일괄 발송 API 호출 실패: ${e.message}`
      }
    }
  }
}
</script>

<style scoped>
.modal-backdrop-custom {
  position: fixed;
  inset: 0;
  background: rgba(0, 0, 0, 0.45);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1050;
}

.modal-box {
  background: #fff;
  border-radius: 0.375rem;
  padding: 1.5rem;
  box-shadow: 0 0.5rem 1rem rgba(0, 0, 0, 0.15);
  min-width: 320px;
  max-width: 500px;
}

table {
  font-size: 0.875rem;
}

.price-input {
  text-align: right;
  font-weight: 500;
}

.price-input::-webkit-outer-spin-button,
.price-input::-webkit-inner-spin-button {
  -webkit-appearance: none;
  margin: 0;
}

.price-input[type=number] {
  -moz-appearance: textfield;
}
</style>
